import * as React from "react";
import { Form, Accordion, Icon, Dropdown, Input } from "semantic-ui-react";
import {
  decodeValueDataFormat,
  encodeValueDataFormat,
  ValueDataFormatType,
  getKoneFormatString
} from "../../utils/pdApiUtils";

export type ValueDataFormatProps = {
  valueDataFormat: number;
  handleDataValueFormatChanged: (value: number) => void;
  permissions : boolean;
  specialValueChanged: (value: number) => void;
  userspecialValue: number;
  /** Optional validation error for preferred values to display in this section */
  preferredValueError?: string;
};

type ValueDataFormatState = {
  isOpen: boolean;
};

/** ValueDataFormat component */
class ValueDataFormat extends React.Component<ValueDataFormatProps, ValueDataFormatState> {
  state = { isOpen: false };

  render(): JSX.Element {
    const { valueDataFormat,permissions, specialValueChanged, userspecialValue, preferredValueError } = this.props;
    const { isOpen } = this.state;
    const decodedValue = decodeValueDataFormat(valueDataFormat);

    // Create range dropdown options
    const rangeOptions = [
      {
        key: 1,
        text: "Number",
        value: "Number"
      },
      {
        key: 2,
        text: "Special",
        value: "Special"
      }
    ];
    // Create mode dropdown options
    const modeOptions = [
      {
        key: 1,
        text: "Editable",
        value: "Editable"
      },
      {
        key: 2,
        text: "Display only",
        value: "Display only"
      }
    ];
    // Create type dropdown options
    const typeOptions = [
      {
        key: 1,
        text: "Unsigned",
        value: "Unsigned"
      },
      {
        key: 2,
        text: "Signed",
        value: "Signed"
      }
    ];
    return (
      <Form.Group grouped={true}>
        <Form.Field>
          <Accordion styled={true} className="edit-accordion">
            <Accordion.Title
              id="valueDataFormat"
              active={isOpen}
              onClick={() => this.setState(() => ({ isOpen: !isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Value data format</b>
              </label>
              {!isOpen && preferredValueError && (
                <p className="errorMessagePadding">{preferredValueError}</p>
              )}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Form.Field
                id="range"
                label="Range"
                control={Dropdown}
                compact={true}
                fluid={true}
                selection={true}
                value={decodedValue.range}
                options={rangeOptions}
                // eslint-disable-next-line
                onChange={({}, data: any) => this.handleRangeChanged(data.value)}
                disabled={permissions}
              />
              {decodedValue.range === "Number" ? (
                <>
                  <Form.Field
                    id="mode"
                    control={Dropdown}
                    label="Mode"
                    compact={true}
                    fluid={true}
                    selection={true}
                    value={decodedValue.mode}
                    options={modeOptions}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any, data: any) =>
                      this.handleValueDataFormatChanged({
                        ...decodedValue,
                        mode: data.value
                      })
                    }
                    disabled={permissions}
                  />
                  <Form.Field
                    id="type"
                    control={Dropdown}
                    label="Type"
                    compact={true}
                    fluid={true}
                    selection={true}
                    value={decodedValue.type}
                    options={typeOptions}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any, data: any) =>
                      this.handleValueDataFormatChanged({
                        ...decodedValue,
                        type: data.value
                      })
                    }
                    disabled={permissions}
                  />
                  <Form.Field
                    id="digit"
                    control={Input}
                    label="Number of digits (0-7)"
                    type="number"
                    min={0}
                    max={7}
                    value={decodedValue.digits}
                    // eslint-disable-next-line
                    onChange={({}, { value }: any) =>
                      this.handleValueDataFormatChanged({
                        ...decodedValue,
                        digits: this.limitValues(value, 0, 7)
                      })
                    }
                    disabled={permissions}
                  />
                  <Form.Field
                    id="decimal"
                    control={Input}
                    label="Number of decimals (0-3)"
                    type="number"
                    min={0}
                    max={3}
                    value={decodedValue.decimals}
                    // eslint-disable-next-line
                    onChange={({}, { value }: any) =>
                      this.handleValueDataFormatChanged({
                        ...decodedValue,
                        decimals: this.limitValues(value, 0, 3)
                      })
                    }
                    disabled={permissions}
                  />
                </>
              ) : (
                <>
                <Form.Field
                  id="specialValue"
                  control={Input}
                  label="Special value (128-255)"
                  type="number"
                  error={(userspecialValue < 128 || userspecialValue > 255)}
                  value={(userspecialValue < 128 || userspecialValue > 255) ? userspecialValue : decodedValue.value}
                  // eslint-disable-next-line
                  onChange={({}, { value }: any) =>{
                    specialValueChanged (value);
                    this.handleValueDataFormatChanged({
                      ...decodedValue,
                      value: value
                    });
                  }}
                  disabled={permissions}
                />
                {isOpen && ((userspecialValue < 128 || userspecialValue > 255)) && <p className="errorMsgDecPadding">{"Special value ranges from 128 to 255"}</p>}
                </>
              )}
              <Form.Field
                id="result"
                disabled={true}
                control={Input}
                label="Result (KONE string / Raw value)"
                readOnly={true}
                value={`${getKoneFormatString(decodedValue)} / ${valueDataFormat}`}
              />
              {isOpen && preferredValueError && (
                <p className="errorMsgDecPadding">{preferredValueError}</p>
              )}
            </Accordion.Content>
            {!isOpen && decodedValue.range !== "Number" && ((userspecialValue < 128 || userspecialValue > 255)) && <p className="errorMessagePadding">{"Special value ranges from 128 to 255"}</p>}                
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Limit value based on given min and max limits
  private limitValues = (value: number, minValue: number, maxValue: number): number => {
    if (value > maxValue) {
      value = maxValue;
    } else if (value < minValue) {
      value = minValue;
    }
    return value;
  };

  // Handler of range changed
  private handleRangeChanged = (range: string): void => {
    let valueDataFormat: ValueDataFormatType;

    if (range === "Number") {
      valueDataFormat = {
        range: "Number",
        mode: "Editable",
        type: "Signed",
        value: 0
      };
    } else {
      valueDataFormat = {
        range: "Special",
        value: 0
      };
    }
    this.handleValueDataFormatChanged(valueDataFormat);
  };

  // Handler of value data format changed
  private handleValueDataFormatChanged = (valueDataFormat: ValueDataFormatType): void => {
    const { handleDataValueFormatChanged } = this.props;
    const value = encodeValueDataFormat(valueDataFormat);
    handleDataValueFormatChanged(value);
  };
}

export default ValueDataFormat;
